import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ChevronDown, ChevronUp, FileText, RotateCcw, Settings } from "lucide-react"
import { SessionSyncControls } from './SessionSyncControls'
import { SessionDisplay } from '@/components/SessionDisplay'
import { useAmendmentSettings } from '@/hooks/admin/useAmendmentSettings'
import { useSyncMutations } from '@/hooks/admin/useSyncMutations'
import type { APISession } from '@/types/session'
import type { DataSource } from '@/hooks/admin/useDatasourceState'
import { useState } from 'react'

interface SyncSettingsCardProps {
  selectedDataSource: DataSource
  apiSessions: APISession[]
  isExpanded: boolean
  onToggleExpanded: () => void
  expandedSessions: Record<string, boolean>
  onToggleSessionExpanded: (session: string) => void
}

export function SyncSettingsCard({
  selectedDataSource,
  apiSessions,
  isExpanded,
  onToggleExpanded,
  expandedSessions,
  onToggleSessionExpanded
}: SyncSettingsCardProps) {
  const amendmentSettings = useAmendmentSettings(selectedDataSource)
  const { syncMarylandBills } = useSyncMutations()
  
  // State for sync controls
  const [selectedSession, setSelectedSession] = useState<string>('')
  const [maxBills, setMaxBills] = useState<number>(10)
  const [maxBillsInput, setMaxBillsInput] = useState<string>('10')

  const {
    syncSettings,
    isMutating,
    handleSyncToggle,
    handleAmendmentSettingChange,
    handleNumericInputChange,
    isSessionSyncEnabled,
    getAmendmentDetectionSettings
  } = amendmentSettings

  return (
    <Card className="border shadow-sm">
      <CardHeader 
        className="pb-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-4 w-4" />
            {selectedDataSource} Sync & Amendment Settings
            <span className="text-sm font-normal text-slate-500">
              ({apiSessions.filter(session => isSessionSyncEnabled(session)).length}/{apiSessions.length} sync)
            </span>
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 pb-3 space-y-4">
          {/* Sync Controls Section */}
          {selectedDataSource === 'Maryland' && (
            <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800/50">
              <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                <RotateCcw className="h-4 w-4" />
                Sync Bills
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                <div className="space-y-2">
                  <Label htmlFor="session-select" className="text-xs text-slate-600 dark:text-slate-400">
                    Session
                  </Label>
                  <Select value={selectedSession} onValueChange={setSelectedSession}>
                    <SelectTrigger id="session-select" className="h-8">
                      <SelectValue placeholder="Select session" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sessions</SelectItem>
                      {apiSessions.map((session) => (
                        <SelectItem key={session} value={session}>
                          <SessionDisplay session={session} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="max-bills" className="text-xs text-slate-600 dark:text-slate-400">
                    Max Bills
                  </Label>
                  <Input
                    id="max-bills"
                    type="number"
                    min="1"
                    max="1000"
                    value={maxBillsInput}
                    onChange={(e) => {
                      const value = e.target.value
                      setMaxBillsInput(value)
                      
                      if (value === '') {
                        // Don't update maxBills when empty, keep the last valid value
                        return
                      }
                      
                      const numValue = parseInt(value)
                      if (!isNaN(numValue) && numValue >= 1) {
                        setMaxBills(numValue)
                      }
                    }}
                    onBlur={() => {
                      // On blur, ensure we have a valid value
                      const value = parseInt(maxBillsInput)
                      if (isNaN(value) || value < 1) {
                        setMaxBills(10)
                        setMaxBillsInput('10')
                      } else {
                        setMaxBills(value)
                        setMaxBillsInput(value.toString())
                      }
                    }}
                    className="h-8"
                    placeholder="10"
                  />
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const sessionParam = selectedSession === 'all' || selectedSession === '' ? undefined : selectedSession
                    syncMarylandBills.mutate({
                      session: sessionParam,
                      maxBills: maxBills
                    })
                  }}
                  disabled={syncMarylandBills.isPending || !selectedSession}
                  className="flex items-center gap-2 h-8"
                >
                  <RotateCcw className={`h-3 w-3 ${syncMarylandBills.isPending ? 'animate-spin' : ''}`} />
                  {syncMarylandBills.isPending ? 'Syncing...' : 'Start Sync'}
                </Button>
              </div>
            </div>
          )}

          {/* Session Configuration Controls */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">Session Configuration</h3>
            {apiSessions.map((session) => {
              const amendmentSettingsForSession = getAmendmentDetectionSettings(session)
              const sessionEnabled = isSessionSyncEnabled(session)
              
              return (
                <SessionSyncControls
                  key={session}
                  session={session}
                  selectedDataSource={selectedDataSource}
                  sessionEnabled={sessionEnabled}
                  amendmentSettings={amendmentSettingsForSession}
                  isExpanded={expandedSessions[session] || false}
                  isMutating={isMutating}
                  onSyncToggle={handleSyncToggle}
                  onToggleExpanded={() => onToggleSessionExpanded(session)}
                  onAmendmentSettingChange={handleAmendmentSettingChange}
                  onNumericInputChange={handleNumericInputChange}
                />
              )
            })}
          </div>

          {/* Compact info panel at bottom */}
          <div className="pt-2 border-t">
            <div className="flex items-start gap-2 text-xs text-slate-500">
              <FileText className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Amendment Detection:</strong> AI vision converts PDFs to images, detects strikethrough/underlined text, identifies amendments. 
                Page comparison always used for efficiency. Results stored in S3.
              </div>
            </div>
          </div>
          
          {syncSettings?.updated_at && (
            <div className="pt-2 border-t">
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-green-500"></div>
                Updated by {syncSettings.updated_by} at {new Date(syncSettings.updated_at).toLocaleString()}
              </p>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
